import { formatTimeStamp } from '@/utils/time'
import { getElColorPrimary } from '@/utils/theme-color'
import { readCssVar } from '@/utils/chart-theme-colors'
import type { GoogleMapsSdk } from './google-maps'

/** webpack 资源 URL（.ts 文件内 ES import 资产会触发 fork-ts-checker TS2307，require 与仓库 main.ts 等先例一致） */
const locationSvgUrl = require('@/assets/location.svg') as string

const CARD_PADDING_X = 16
const MIN_CARD_WIDTH = 56
const MAX_CARD_WIDTH = 200

/** 钉下信息卡行 */
export interface MarkerIconLine {
  text: string
  /** 加粗（首行计算机名/时间用） */
  bold?: boolean
  /** 行样式：primary=主文字色，secondary/tertiary=次要文字色 */
  kind?: 'primary' | 'secondary' | 'tertiary'
}

export interface MarkerIconOptions {
  lines: MarkerIconLine[]
  /** 图钉缩放，默认 1.25（最新位置 1.5） */
  pinScale?: number
  /** 图钉填充色（覆盖主题色，离线灰场景传入） */
  pinFill?: string
}

export interface MarkerIconSpec {
  url: string
  size: unknown
  scaledSize: unknown
  anchor: unknown
}

let locationSvgTextPromise: Promise<string> | null = null

/** 读取 location.svg 源码（与钉下信息卡合成 data URI），全局缓存 */
export function loadLocationSvgText(): Promise<string> {
  if (!locationSvgTextPromise) {
    locationSvgTextPromise = fetch(locationSvgUrl)
      .then(res => res.text())
      .catch(() => '')
  }
  return locationSvgTextPromise
}

/** SVG data URI 内不支持 CSS 变量，绘制时读取运行时主题值（回退为 Element 默认值） */
export function getMarkerPalette(): {
  primary: string
  cardBackground: string
  pinInner: string
  textPrimary: string
  textSecondary: string
  borderLight: string,
} {
  const isDark = document.documentElement.classList.contains('dark')

  const cardBackground = readCssVar(
    '--el-bg-color',
    isDark ? '#1D1E1F' : '#FFFFFF'
  )

  return {
    primary: getElColorPrimary(),
    cardBackground,
    pinInner: readCssVar(
      '--el-color-white',
      isDark ? '#1D1E1F' : '#FFFFFF'
    ),
    textPrimary: readCssVar(
      '--el-text-color-primary',
      isDark ? '#E5EAF3' : '#303133'
    ),
    textSecondary: readCssVar(
      '--el-text-color-secondary',
      isDark ? '#A3A6AD' : '#909399'
    ),
    borderLight: readCssVar(
      '--el-border-color-lighter',
      isDark ? '#414243' : '#EBEEF5'
    )
  }
}

export function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

/** 估算文本宽度（SVG 无法自动测量文本）：全角字符按 11px、半角按 6.4px（font-size 11） */
export function estimateTextWidth(text: string): number {
  let width = 0
  for (const char of text) {
    width += char.charCodeAt(0) > 0x2E7F ? 11 : 6.4
  }
  return Math.ceil(width)
}

/** 提取 location.svg 内部标记（去掉 <svg> 外层），并把锚点色 #409EFF 替换为目标填充色；加载失败时回退为圆形图钉 */
export function extractPinInner(pinSvgText: string, pinFill: string, white: string): string {
  const match = /<svg[^>]*>([\s\S]*?)<\/svg>/i.exec(pinSvgText)
  const inner = match ? match[1] : ''
  if (inner) {
    return inner.replace(/#409EFF/gi, pinFill)
  }
  return `<circle cx="12" cy="12" r="10" fill="${pinFill}"/><circle cx="12" cy="12" r="4" fill="${white}"/>`
}

/** 钉下卡片时间文案（紧凑月-日 时分秒） */
export function formatPointTime(locationTime: string): string {
  return formatTimeStamp(locationTime, 'MM-DD HH:mm:ss') || '--'
}

/** 标点图标：src/assets/location.svg 图钉 + 钉下信息卡（1/2/3 行，宽度按内容自适应），锚点在针尖 */
export function buildMarkerIcon(maps: GoogleMapsSdk, pinSvgText: string, options: MarkerIconOptions): MarkerIconSpec {
  const palette = getMarkerPalette()
  const scale = options.pinScale ?? 1.25
  const pinInner = extractPinInner(pinSvgText,options.pinFill ?? palette.primary,palette.pinInner)
  const lines = options.lines.filter(line => line.text)
  const cardHeight = lines.length === 1 ? 22 : 44 + (lines.length - 2) * 16
  const baselineAt = (index: number) => (lines.length === 1 ? 63 : 66 + index * 16)
  const lineColor = (line: MarkerIconLine) =>
    line.kind && line.kind !== 'primary' ? palette.textSecondary : palette.textPrimary
  const cardWidth = Math.min(
    MAX_CARD_WIDTH,
    Math.max(MIN_CARD_WIDTH, Math.max(...lines.map(item => estimateTextWidth(item.text))) + CARD_PADDING_X)
  )
  const svgWidth = cardWidth + 20
  const svgHeight = Math.max(96, 48 + cardHeight + 4)
  const centerX = svgWidth / 2
  const cardX = (svgWidth - cardWidth) / 2
  const parts = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}">`,
    `<g transform="translate(${centerX - 12 * scale},${42 - 24 * scale}) scale(${scale})">${pinInner}</g>`,
    `<rect x="${cardX}" y="48" width="${cardWidth}" height="${cardHeight}" rx="6" fill="${palette.cardBackground}" stroke="${palette.borderLight}" stroke-width="1"/>`
  ]
  lines.forEach((line, index) => {
    parts.push(
      `<text x="${centerX}" y="${baselineAt(index)}" text-anchor="middle" font-size="11" font-weight="${line.bold ? 600 : 400}" fill="${lineColor(line)}">${escapeXml(line.text)}</text>`
    )
  })
  parts.push('</svg>')
  const svg = parts.join('')
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    // Google Maps 约定：使用 scaledSize 时必须同时声明 size（原始尺寸），否则部分渲染路径不显示图标
    size: new maps.Size(svgWidth, svgHeight),
    scaledSize: new maps.Size(svgWidth, svgHeight),
    anchor: new maps.Point(centerX, 42)
  }
}
